import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, 
    TextField, Box, Typography, CircularProgress, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { formatDateDisplay } from '../../utils/dateUtils'; 
import * as API from '../../api';

const BOOKING_CATEGORY_LABELS = {
    'TOUR': 'Tour trọn gói / Ghép',
    'TRANSPORT': 'Dịch vụ Thuê xe',
    'HOTEL': 'Dịch vụ Phòng KS',
    'FLIGHT': 'Vé máy bay',
    'COMBO': 'Combo Dịch vụ',
    'OTHER': 'Dịch vụ Khác'
};

// --- CÁC HÀM HELPER GIỮ NGUYÊN ---

const formatHeaderDate = (bookings) => {
  if (!bookings || bookings.length === 0) return '';
  
  const distinctDates = [...new Set(bookings.map(b => b.start_date))].sort();
  
  if (distinctDates.length === 0) return '';
  if (distinctDates.length === 1) return formatDateDisplay(distinctDates[0]);

  const start = distinctDates[0];
  const end = distinctDates[distinctDates.length - 1];
  
  const [y1, m1, d1] = start.split('-');
  const [y2, m2, d2] = end.split('-');

  if (y1 === y2 && m1 === m2) {
    return `${d1}-${d2}/${m1}/${y1}`;
  }
  
  return `${d1}/${m1}/${y1} - ${d2}/${m2}/${y2}`;
};

const getTransportInfo = (bk, tours) => {
    const tourInfo = tours.find(t => t.tour_code === bk.tour);
    const defaultCarType = tourInfo ? (tourInfo.transport_option || 'Theo tour') : 'Theo tour';
    
    const includedTransports = (bk.tour_transports || []).filter(t => t.payment_method === 'INCLUDED');

    if (includedTransports.length === 0) {
        return { 
            carTypeText: defaultCarType, 
            pickupText: 'Khách tự túc',
            carTypeHtml: defaultCarType, 
            pickupHtml: 'Khách tự túc'
        };
    }

    const carTypes = [];
    const pickups = [];

    includedTransports.forEach(t => {
        let dirLabel = '';
        if (t.direction === 'ONE_WAY_TO') dirLabel = '[Đi]';
        else if (t.direction === 'ONE_WAY_FROM') dirLabel = '[Về]';
        else if (t.direction === 'ROUND_TRIP') dirLabel = '[2 Chiều]';

        const vType = t.vehicle_type || 'Xe';
        carTypes.push(`${dirLabel} ${vType}`);

        let timeStr = '';
        if (t.pickup_datetime) {
            const parts = t.pickup_datetime.split('T');
            if (parts.length > 1) {
                timeStr = ` (${parts[1].substring(0, 5)})`;
            }
        }
        const point = t.pickup_point || '???';
        const drop = t.dropoff_point || '???';
        pickups.push(`${dirLabel} Đón: ${point} - Trả: ${drop}${timeStr}`);
    });

    return { 
        carTypeText: carTypes.join(', '), 
        pickupText: pickups.join(' | '),
        carTypeHtml: carTypes.join('<br/>'), 
        pickupHtml: pickups.join('<br/>')
    };
};

const getPaxInfo = (bk) => {
    const adults = bk.adults_count || 0;
    const children = bk.children_count || 0;
    const total = adults + children;

    if (children === 0) {
        return `${adults} Khách NL`;
    } else {
        return `${total} Khách (${adults} NL, ${children} TE)`;
    }
};

const getRoomInfo = (bk) => {
    if (!bk.tour_rooms || bk.tour_rooms.length === 0) return '';
    const details = bk.tour_rooms.map(r => `   - ${r.room_type} (x${r.quantity})${r.note ? ': ' + r.note : ''}`).join('\n');
    return `Phòng/Cabin:\n${details}`;
};

const getExtraInfo = (bk) => {
    if (!bk.tour_extras || bk.tour_extras.length === 0) return '';
    const details = bk.tour_extras.map(e => `   - ${e.title} (x${e.quantity})${e.note ? ': ' + e.note : ''}`).join('\n');
    return `Dịch vụ khác:\n${details}`;
};

const getTransportDetails = (bk) => {
    if (!bk.tour_transports || bk.tour_transports.length === 0) return '';
    const includedTransports = bk.tour_transports.filter(t => t.payment_method === 'INCLUDED');
    
    if (includedTransports.length === 0) return 'Khách tự túc di chuyển';

    return includedTransports.map((t, index) => {
        let dirLabel = 'Xe';
        if (t.direction === 'ONE_WAY_TO') dirLabel = 'Chiều đi';
        else if (t.direction === 'ONE_WAY_FROM') dirLabel = 'Chiều về';
        else if (t.direction === 'ROUND_TRIP') dirLabel = 'Hai chiều';
        
        let timeStr = '';
        if (t.pickup_datetime) {
            const parts = t.pickup_datetime.split('T');
            if (parts.length > 1) {
                timeStr = ` (${parts[1].substring(0, 5)})`;
            }
        }

        const supplierName = t.supplier && t.supplier.name ? t.supplier.name : '';
        const driverInfo = t.driver_info ? ` - LX: ${t.driver_info}` : '';
        const noteInfo = t.note ? ` - Note: ${t.note}` : '';
        
        return `   ${index + 1}. ${dirLabel}: ${t.vehicle_type || 'Xe'} ${supplierName ? `(${supplierName})` : ''}\n      Đón: ${t.pickup_point || '???'}${timeStr} - Trả: ${t.dropoff_point || '???'}${driverInfo}${noteInfo}`;
    }).join('\n');
};

// --- MẪU TEXT (DÙNG CHO COPY ZALO) ---
const generateTextTemplate = (bookings, tours) => {
    if (!bookings || bookings.length === 0) return '';
    
    // Trường hợp 1 Booking lẻ
    if (bookings.length === 1) {
        const booking = bookings[0];
        const transportDetails = getTransportDetails(booking);
        const dateDisplay = formatDateDisplay(booking.start_date);
        const tourInfo = tours.find(t => t.tour_code === booking.tour);
        const catLabel = BOOKING_CATEGORY_LABELS[booking.booking_category] || 'Dịch vụ lẻ';
        const baseTourName = tourInfo ? (tourInfo.base_tour_name || tourInfo.name) : catLabel;
        const tourSchedule = tourInfo ? (tourInfo.base_tour_name || tourInfo.name) : catLabel;

        let guestDetails = '';
        if (booking.parent_guests && booking.parent_guests.length > 0) {
            guestDetails = booking.parent_guests.map((g, i) => {
                const typeLabel = g.guest_type === 'CHILD' ? 'TE' : (g.guest_type === 'ADULT' ? 'NL' : '');
                const nameWithType = typeLabel ? `${g.name} (${typeLabel})` : g.name;
                
                const dob = g.dob ? formatDateDisplay(g.dob) : '';
                const nat = g.nationality ? g.nationality : '';
                const pp = g.passport ? g.passport : '';
                const details = [nameWithType, dob, nat, pp].filter(Boolean).join(' - ');
                return `  ${i + 1}. ${details}`;
            }).join('\n');
        }

        const roomInfo = getRoomInfo(booking);
        const extraInfo = getExtraInfo(booking);
        const header = `[Fola Travel BK] Đặt booking ${baseTourName} - ${dateDisplay} - 1 BK`;

        let content = `${header}
Kính gửi Quý Công ty,
Công ty du lịch Fola Travel xin gửi thông tin booking như sau:
-----------------------------------------
Lịch trình / Dịch vụ: ${tourSchedule}
Mã booking: ${booking.bk_code}
Tên khách lead: ${booking.customer_detail?.name || 'Khách lẻ'}
SĐT: ${booking.customer_detail?.phone || ''}
Thông tin khách: ${getPaxInfo(booking)}
${guestDetails}`;

        if (roomInfo) content += `\n${roomInfo}`;
        content += `\nNgày đi: ${dateDisplay}`;
        if (transportDetails) content += `\nThông tin đón trả:\n${transportDetails}`;
        if (extraInfo) content += `\n${extraInfo}`;

        // SỬA: Dùng parent_special_request
        content += `\nNote: ${booking.parent_special_request || 'Không'}
Đại lý: Fola Travel - 0363463666 hoặc 0986332366 (Mr. Hùng).
Quý công ty vui lòng xác nhận đặt chỗ tại đây.
Trân trọng,`;
        return content;
    }

    // Trường hợp Nhiều Booking (Gộp)
    const firstTourInfo = tours.find(t => t.tour_code === bookings[0].tour);
    const catLabel = BOOKING_CATEGORY_LABELS[bookings[0].booking_category] || 'Dịch vụ lẻ';
    const baseTourName = firstTourInfo ? (firstTourInfo.base_tour_name || firstTourInfo.name) : catLabel;
    const dateRangeStr = formatHeaderDate(bookings);

    let text = `[Fola Travel BK] Đặt booking ${baseTourName} - ${dateRangeStr} - ${bookings.length} BK\n`;
    text += `Kính gửi Quý đối tác, Fola Travel gửi danh sách đoàn:\n`;
    text += `==================================\n`;

    const sortedBookings = [...bookings].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    const grouped = {};

    sortedBookings.forEach(bk => {
        const d = bk.start_date;
        if (!grouped[d]) grouped[d] = [];
        grouped[d].push(bk);
    });

    Object.keys(grouped).forEach((dateKey, groupIndex) => {
        const groupBookings = grouped[dateKey];
        const dateDisplay = formatDateDisplay(dateKey);

        if (groupIndex > 0) text += `----------------------------------\n`;
        text += `Ngày đi: ${dateDisplay} (${groupBookings.length} BK)\n`;

        groupBookings.forEach((bk, index) => {
            const hasTransport = bk.transports && bk.transports.length > 0;
            const { carTypeText, pickupText } = getTransportInfo(bk, tours);
            const roomInfo = getRoomInfo(bk);
            const extraInfo = getExtraInfo(bk);
            
            // SỬA: Dùng bk.bk_code thay vì bk.booking_code
            text += `${index + 1}. [${bk.bk_code}] ${bk.customer_detail?.name || 'Khách'} - ${bk.customer_detail?.phone || 'No Phone'} - ${getPaxInfo(bk)}`;
            
            if (hasTransport) {
                text += `\n   Xe: ${carTypeText}`;
                text += `\n   Đón/Trả: ${pickupText}`;
            }
            if (roomInfo) text += `\n${roomInfo}`;
            if (extraInfo) text += `\n${extraInfo}`;
            
            // SỬA: Dùng parent_special_request
            if (bk.parent_special_request) text += `\n   Note: ${bk.parent_special_request}`;
            text += `\n`;
        });
    });

    text += `==================================\n`;
    text += `Đại lý: Fola Travel - 0363463666 hoặc 0986332366 (Mr. Hùng).\nQuý công ty vui lòng xác nhận đặt chỗ tại đây.\nTrân trọng,`;

    return text;
};

// --- MẪU HTML (DÙNG CHO EMAIL) ---
const formatGuestDetailsHtml = (guests) => {
    if (!guests || guests.length === 0) return '';
    return guests.map(g => {
        // Ánh xạ guest_type sang chữ viết tắt
        const typeLabel = g.guest_type === 'CHILD' ? 'TE' : (g.guest_type === 'ADULT' ? 'NL' : '');
        const nameWithType = typeLabel ? `${g.name} (${typeLabel})` : g.name;
        
        const parts = [
            nameWithType,
            g.dob ? formatDateDisplay(g.dob) : '',
            g.nationality,
            g.passport
        ].filter(Boolean);
        return parts.join(' - ');
    }).join('<br/>');
};

const formatRoomDetailsHtml = (rooms) => {
    if (!rooms || rooms.length === 0) return '';
    return rooms.map(r => `• ${r.room_type} (x${r.quantity})${r.note ? `: ${r.note}` : ''}`).join('<br/>');
};

const generateHtmlTemplate = (bookings, tours) => {
    if (!bookings || bookings.length === 0) return '';

    const firstTourInfo = tours.find(t => t.tour_code === bookings[0].tour);
    const catLabel = BOOKING_CATEGORY_LABELS[bookings[0].booking_category] || 'Dịch vụ lẻ';
    const baseTourName = firstTourInfo ? (firstTourInfo.base_tour_name || firstTourInfo.name) : catLabel;
    const hasAnyRooms = bookings.some(bk => bk.rooms && bk.rooms.length > 0);
    
    // Style Inline
    const tableStyle = `width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; font-size: 13px;`;
    const thStyle = `border: 1px solid #333; padding: 5px; background-color: #f2f2f2; text-align: center; font-weight: bold;`;
    const tdStyle = `border: 1px solid #333; padding: 5px; vertical-align: top;`;

    const rows = bookings.map(bk => {
        const { carTypeHtml, pickupHtml } = getTransportInfo(bk, tours);
        const guestHtml = formatGuestDetailsHtml(bk.parent_guests);
        const roomHtml = formatRoomDetailsHtml(bk.tour_rooms);
        const paxInfo = getPaxInfo(bk);
        const startDate = formatDateDisplay(bk.start_date);

        // SỬA: Dùng bk.bk_code và bk.parent_special_request
        return `
        <tr>
            <td style="${tdStyle}; font-weight: bold; color: #007373;">${bk.bk_code}</td> 
            <td style="${tdStyle}">${bk.customer_detail?.name || ''}</td>
            <td style="${tdStyle}">${bk.customer_detail?.phone || ''}</td>
            <td style="${tdStyle}">${paxInfo}</td>
            <td style="${tdStyle}">${guestHtml}</td>
            ${hasAnyRooms ? `<td style="${tdStyle}">${roomHtml}</td>` : ''}
            <td style="${tdStyle}; text-align: center;">${startDate}</td>
            <td style="${tdStyle}">${carTypeHtml}</td>
            <td style="${tdStyle}">${pickupHtml}</td>
            <td style="${tdStyle}">${bk.parent_special_request || 'Không'}</td>
        </tr>
        `;
    }).join('');

    return `<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6;">
    <div>Kính gửi Quý Công ty,</div>
    <div style="margin-bottom: 10px;">Công ty du lịch Fola Travel xin gửi thông tin ${bookings.length} booking như sau:</div>
    <div style="margin-bottom: 10px;">--------------------------------------------------</div>
    <div style="margin-bottom: 10px;"><strong>Lịch trình tour:</strong> ${baseTourName}</div>

    <table style="${tableStyle}">
        <thead>
            <tr>
                <th style="${thStyle}">Mã BK</th> <th style="${thStyle}">Tên đại diện</th>
                <th style="${thStyle}">SĐT</th>
                <th style="${thStyle}">SL khách</th>
                <th style="${thStyle} width: 25%;">Thông tin khách</th>
                ${hasAnyRooms ? `<th style="${thStyle}">Hạng phòng</th>` : ''}
                <th style="${thStyle}">Ngày đi</th>
                <th style="${thStyle}">DV xe</th>
                <th style="${thStyle}">Điểm đón/trả</th>
                <th style="${thStyle}">Yêu cầu</th>
            </tr>
        </thead>
        <tbody>
            ${rows}
        </tbody>
    </table>

    <div style="margin-top: 15px;"><strong>Đại lý: Fola Travel</strong></div>
    <div>--------------------------------------------------</div>
    <div style="margin-top: 10px;">Mọi thông tin chi tiết vui lòng liên hệ số hotline Fola Travel tại 0363463666 hoặc 0986332366 (Mr. Hùng).</div>
    <div>Quý công ty vui lòng xác nhận đặt chỗ qua email này.</div>
    <div style="margin-top: 10px;">Trân trọng,</div>
</body>
</html>`;
};

// --- COMPONENT CHÍNH ---
const TourOrderDialog = ({ open, onClose, selectedBookings, tours, onViewDetail, showToast, onSuccess }) => {
    
    // State cho dữ liệu
    const [textContent, setTextContent] = useState(''); // Nội dung Copy
    const [htmlContent, setHtmlContent] = useState(''); // Nội dung Email (HTML)
    
    // State cho form
    const [recipientEmail, setRecipientEmail] = useState('');
    const [ccEmail, setCcEmail] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    
    // State UI
    const [viewMode, setViewMode] = useState('EMAIL'); // 'EMAIL' (Default) hoặc 'TEXT'
    const [sending, setSending] = useState(false);

    // KHI MỞ DIALOG: KHỞI TẠO DỮ LIỆU
    useEffect(() => {
        if (open && selectedBookings.length > 0) {
            const firstBk = selectedBookings[0];
            const tourInfo = tours.find(t => t.tour_code === firstBk.tour);

            // 1. Tạo nội dung Text
            setTextContent(generateTextTemplate(selectedBookings, tours));

            // 2. Tạo nội dung HTML
            setHtmlContent(generateHtmlTemplate(selectedBookings, tours));

            // 3. Lấy Email NCC (nếu có)
            let defaultEmail = '';
            let defaultCc = '';
            let baseTourName = '';

            if (tourInfo) {
                defaultEmail = tourInfo.supplier_email || '';
                defaultCc = tourInfo.supplier_cc_emails || '';
                baseTourName = tourInfo.base_tour_name || tourInfo.name;
            } else {
                // Nếu không có tour, dùng Tên Danh mục làm tên
                baseTourName = BOOKING_CATEGORY_LABELS[firstBk.booking_category] || 'Dịch vụ lẻ';
                
                // Thử lấy email dựa trên loại Booking
                if (firstBk.booking_category === 'TRANSPORT' && firstBk.transports && firstBk.transports.length > 0) {
                     defaultEmail = firstBk.transports[0].supplier_email || '';
                     defaultCc = firstBk.transports[0].supplier_cc_emails || '';
                } else if (firstBk.booking_category === 'HOTEL' && firstBk.rooms && firstBk.rooms.length > 0) {
                     defaultEmail = firstBk.rooms[0].supplier_email || '';
                     defaultCc = firstBk.rooms[0].supplier_cc_emails || '';
                } else if (firstBk.booking_category === 'OTHER' && firstBk.extras && firstBk.extras.length > 0) {
                     defaultEmail = firstBk.extras[0].supplier_email || '';
                     defaultCc = firstBk.extras[0].supplier_cc_emails || '';
                }
            }

            setRecipientEmail(defaultEmail);
            setCcEmail(defaultCc);

            // 4. Tạo tiêu đề
            const dateRangeStr = formatHeaderDate(selectedBookings);
            
            if (selectedBookings.length === 1) {
                const customerName = selectedBookings[0].customer_detail?.name || 'Khách lẻ';
                setEmailSubject(`[Fola Travel BK] ${baseTourName} - ${dateRangeStr} - 1 BK - ${customerName}`);
            } else {
                setEmailSubject(`[Fola Travel BK] ${baseTourName} - ${dateRangeStr} - ${selectedBookings.length} BK`);
            }
            
            // Mặc định về tab Email
            setViewMode('EMAIL');
        }
    }, [open, selectedBookings, tours]);

    // XỬ LÝ: COPY TEXT
    const handleCopy = () => {
        navigator.clipboard.writeText(textContent);
        if (showToast) {
            showToast('Đã copy nội dung (Dạng Text)!', 'success');
        }
    };

    // XỬ LÝ: GỬI EMAIL
    const handleSendEmail = async () => {
        if (!recipientEmail) {
            if (showToast) showToast('Vui lòng nhập Email người nhận!', 'error');
            return;
        }

        setSending(true);
        try {
            // Gửi nội dung từ state htmlContent (đã cho phép edit)
            await API.client.post('/bookings/send_tour_order_email/', {
                email: recipientEmail,
                cc: ccEmail,
                subject: emailSubject,
                content: htmlContent,
                // SỬA: Lấy bk_code và dùng Set để lọc trùng lặp nếu nhiều Tour nằm trong cùng 1 Booking
                booking_codes: [...new Set(selectedBookings.map(bk => bk.bk_code))]
            });
            
            // Cập nhật trạng thái BookingTour -> Đã liên hệ
            const updatePromises = selectedBookings.map(bk => 
                API.updateBookingTourStatus(bk.id, 'CONTACTED')
            );
            
            await Promise.all(updatePromises);

            if (showToast) showToast(`Đã gửi email thành công tới ${recipientEmail}!`, 'success');
            if (onSuccess) onSuccess(); 
            
            onClose();
        } catch (error) {
            console.error(error);
            if (showToast) showToast('Gửi mail thất bại. Vui lòng kiểm tra lại.', 'error');
        } finally {
            setSending(false);
        }
    };

    const handleViewChange = (event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            {/* 1. Header: Bỏ Toggle Button, chỉ giữ Title */}
            <DialogTitle sx={{ bgcolor: '#007373', color: 'white' }}>
                <Typography variant="h6">LỆNH ĐIỀU TOUR ({selectedBookings.length} Booking)</Typography>
            </DialogTitle>
            
            <DialogContent dividers>
                <Box sx={{ display: 'flex', gap: 2, height: '480px' }}>
                    
                {/* Cột trái: Danh sách booking (Đã sửa ID và mã hiển thị) */}
                    <Box sx={{ width: '25%', borderRight: '1px solid #eee', pr: 2, overflowY: 'auto' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Danh sách Booking:
                        </Typography>
                        {selectedBookings.map((bk, i) => (
                            // SỬA: Dùng bk.id làm key (mỗi dịch vụ Tour có 1 ID duy nhất)
                            <Box key={bk.id} sx={{ p: 1, mb: 1, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #ddd' }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ color: '#007373', mb: 0.5 }}>
                                    {/* SỬA: Gọi bk_code để hiển thị */}
                                    #{i + 1} - {bk.customer_detail?.name || 'Khách'} - {bk.bk_code}
                                </Typography>
                                <Button 
                                    size="small" variant="outlined" 
                                    sx={{fontSize: '0.7rem', py: 0}}
                                    // SỬA: Truyền bk_code cho nút xem chi tiết
                                    onClick={() => onViewDetail(bk.bk_code)}
                                >
                                    Xem BK
                                </Button>
                            </Box>
                        ))}
                    </Box>
                    
                    {/* Cột phải: Cấu trúc lại thứ tự */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        
                        {/* A. Tiêu đề lên đầu */}
                         <Box>
                             <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>
                                 Tiêu đề:
                             </Typography>
                             <TextField 
                                fullWidth size="small" 
                                value={emailSubject} 
                                onChange={(e) => setEmailSubject(e.target.value)}
                                placeholder="Tiêu đề..."
                             />
                        </Box>

                        {/* B. Phần nội dung & Nút chuyển đổi */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            {/* Thanh công cụ: Label + Toggle Button */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>
                                    {viewMode === 'EMAIL' ? 'Nội dung Email:' : 'Nội dung Text:'}
                                </Typography>
                                
                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={handleViewChange}
                                    size="small"
                                    sx={{ bgcolor: '#f5f5f5', height: '32px' }}
                                >
                                    <ToggleButton value="EMAIL" sx={{ px: 2, fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'none' }}>
                                        <EmailIcon sx={{ mr: 1, fontSize: '1rem' }} /> Email (Bảng)
                                    </ToggleButton>
                                    <ToggleButton value="TEXT" sx={{ px: 2, fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'none' }}>
                                        <TextSnippetIcon sx={{ mr: 1, fontSize: '1rem' }} /> Text (Copy)
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            
                            {viewMode === 'EMAIL' ? (
                                <TextField
                                    multiline
                                    fullWidth
                                    value={htmlContent}
                                    onChange={(e) => setHtmlContent(e.target.value)}
                                    sx={{ 
                                        flex: 1, 
                                        bgcolor: '#fff3e0', // Màu nền cam nhạt cho HTML
                                        '& .MuiInputBase-root': {
                                            height: '100%',
                                            alignItems: 'flex-start',
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem'
                                        }
                                    }}
                                />
                            ) : (
                                <TextField
                                    multiline
                                    fullWidth
                                    value={textContent}
                                    onChange={(e) => setTextContent(e.target.value)}
                                    sx={{ 
                                        flex: 1, 
                                        bgcolor: '#f9fbe7', // Màu nền xanh nhạt cho Text
                                        '& .MuiInputBase-root': {
                                            height: '100%',
                                            alignItems: 'flex-start',
                                            fontFamily: 'monospace',
                                            fontSize: '0.9rem'
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            {/* C. Footer: Thêm ô nhập Email vào đây */}
            <DialogActions sx={{ p: 2, justifyContent: 'space-between', bgcolor: '#f9f9f9' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                    {viewMode === 'EMAIL' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField 
                                fullWidth size="small" 
                                label="Gửi tới Email (To)"
                                value={recipientEmail} 
                                onChange={(e) => setRecipientEmail(e.target.value)}
                                placeholder="nhacungcap@example.com"
                                type="email"
                                variant="outlined"
                                sx={{ bgcolor: 'white' }}
                            />
                            <TextField 
                                fullWidth size="small" 
                                label="CC Email (Ngăn cách nhiều email bằng dấu phẩy)"
                                value={ccEmail} 
                                onChange={(e) => setCcEmail(e.target.value)}
                                placeholder="sale1@example.com, dieuhanh@example.com"
                                variant="outlined"
                                sx={{ bgcolor: 'white' }}
                            />
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={onClose} color="inherit" disabled={sending}>Đóng</Button>
                    
                    {viewMode === 'TEXT' && (
                        <Button 
                            variant="contained" 
                            color="success"
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopy} 
                            disabled={sending}
                        >
                            Copy Nội dung
                        </Button>
                    )}

                    {viewMode === 'EMAIL' && (
                        <Button 
                            variant="contained" 
                            startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                            onClick={handleSendEmail}
                            disabled={sending || !recipientEmail}
                            sx={{ bgcolor: '#ed6c02', '&:hover': {bgcolor: '#e65100'} }}
                        >
                            {sending ? 'Đang gửi...' : 'Gửi Email'}
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default TourOrderDialog;